import React, { useMemo, useState, useCallback, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axiosConfig";

// ─── CONTEXT IMPORTS (unchanged) ─────────────────────────────────────────────
import { useProjects }      from "../context/ProjectContext";
import { useTasks }         from "../context/TaskContext";
import { usePurchaseOrder } from "../context/PurchaseOrderContext";
import { useVendors }       from "../context/VendorContext";
import { useInventory }     from "../context/InventoryContext";
import { useCRM }           from "../context/CRMContext";
import { useEquipment }     from "../context/EquipmentContext";
import { useConsumption }   from "../context/ConsumptionContext";
import { useEmployees }     from "../context/EmployeeContext";
import { useAuth }          from "../context/AuthContext";
import { SettingsContext }  from "../context/SettingsContext";

// ─── RECHARTS ─────────────────────────────────────────────────────────────────
import {
  ResponsiveContainer,
  PieChart, Pie, Cell,
  AreaChart, Area,
  BarChart, Bar,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  RadialBarChart, RadialBar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ComposedChart, Line,
} from "recharts";

// ─── ICONS ────────────────────────────────────────────────────────────────────
import {
  ClipboardDocumentCheckIcon, CurrencyDollarIcon, BriefcaseIcon,
  ShoppingCartIcon, ExclamationTriangleIcon, DocumentTextIcon,
  CubeIcon, UserGroupIcon, FunnelIcon, PlusIcon, TruckIcon,
  ShieldCheckIcon, FolderIcon, ArrowPathIcon, TrashIcon,
  BuildingOfficeIcon, UsersIcon, MoonIcon, SunIcon,
  ArrowUpIcon, ArrowDownIcon, ChartBarIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";

// ═══════════════════════════════════════════════════════════════════════════════
// ■ CONSTANTS & HELPERS
// ═══════════════════════════════════════════════════════════════════════════════
const PALETTE = [
  "#6366f1","#10b981","#f59e0b","#f43f5e",
  "#06b6d4","#8b5cf6","#f97316","#14b8a6","#ec4899","#84cc16",
];

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const fmt = {
  cr:  (v) => `₹${((Number(v) || 0) / 1e7).toFixed(2)} Cr`,
  lk:  (v) => `₹${((Number(v) || 0) / 1e5).toFixed(1)}L`,
  pct: (v) => `${(Number(v) || 0).toFixed(1)}%`,
  num: (v) => (Number(v) || 0).toLocaleString("en-IN"),
};

const getDateThreshold = (range) => {
  const map = { "7d": 7, "30d": 30, "90d": 90, "365d": 365 };
  return Date.now() - (map[range] || 365) * 86400000;
};

const getPrevThreshold = (range) => {
  const map = { "7d": 14, "30d": 60, "90d": 180, "365d": 730 };
  return Date.now() - (map[range] || 730) * 86400000;
};

const parseDate = (d) => {
  if (!d) return 0;
  const t = new Date(d).getTime();
  return isNaN(t) ? 0 : t;
};

// ─── MOCK FALLBACK DATA ────────────────────────────────────────────────────────
const MOCK_REVENUE = [
  { month:"Jan", revenue:4850000, cost:3200000, po:1200000 },
  { month:"Feb", revenue:5200000, cost:3600000, po:1800000 },
  { month:"Mar", revenue:4600000, cost:3100000, po:1400000 },
  { month:"Apr", revenue:6100000, cost:4100000, po:2100000 },
  { month:"May", revenue:5800000, cost:3900000, po:1900000 },
  { month:"Jun", revenue:7200000, cost:4800000, po:2600000 },
  { month:"Jul", revenue:6800000, cost:4500000, po:2300000 },
  { month:"Aug", revenue:7900000, cost:5100000, po:2900000 },
  { month:"Sep", revenue:8300000, cost:5400000, po:3100000 },
  { month:"Oct", revenue:7600000, cost:5000000, po:2700000 },
  { month:"Nov", revenue:8900000, cost:5800000, po:3400000 },
  { month:"Dec", revenue:9400000, cost:6100000, po:3800000 },
];

const MOCK_PROJECTS_BUDGET = [
  { name:"Highway NH-48",   budget:42000000, actual:36800000 },
  { name:"Metro Phase 3",   budget:85000000, actual:61200000 },
  { name:"Skyline Tower",   budget:31000000, actual:29400000 },
  { name:"Industrial Park", budget:19500000, actual:14100000 },
  { name:"Bridge Repair",   budget:12000000, actual:11800000 },
  { name:"IT Campus",       budget:55000000, actual:38700000 },
];

const MOCK_VENDOR_RADAR = [
  { vendor:"Tata Steel",  delivery:88, quality:92, price:78, reliability:90, comm:85 },
  { vendor:"L&T Infra",   delivery:75, quality:88, price:82, reliability:80, comm:79 },
  { vendor:"JSW Cement",  delivery:91, quality:85, price:70, reliability:87, comm:83 },
];

const MOCK_EQUIPMENT_USAGE = [
  { type:"Excavator",      utilization:84, idle:16 },
  { type:"Crane",          utilization:71, idle:29 },
  { type:"Bulldozer",      utilization:66, idle:34 },
  { type:"Mixer",          utilization:90, idle:10 },
  { type:"Roller",         utilization:58, idle:42 },
  { type:"Dumper",         utilization:77, idle:23 },
];

const MOCK_INVENTORY = [
  { category:"Cement",      quantity:1840, minStock:500  },
  { category:"Steel Bars",  quantity:620,  minStock:300  },
  { category:"Sand",        quantity:2300, minStock:800  },
  { category:"Bricks",      quantity:8500, minStock:2000 },
  { category:"Electrical",  quantity:310,  minStock:200  },
  { category:"Plumbing",    quantity:180,  minStock:250  },
  { category:"Paint",       quantity:440,  minStock:150  },
  { category:"Glass",       quantity:90,   minStock:100  },
];

// ═══════════════════════════════════════════════════════════════════════════════
// ■ THEME BUILDER
// ═══════════════════════════════════════════════════════════════════════════════
const makeTheme = (dark) =>
  dark
    ? {
        page:         "bg-[#0d1117] text-slate-100",
        card:         "bg-[#161b22] border-[#30363d] shadow-xs text-white",
        cardHover:    "hover:border-slate-600 hover:shadow-md",
        subheader:    "bg-[#21262d] border-b border-[#30363d]",
        text:         "text-white",
        textMuted:    "text-slate-400",
        textFaint:    "text-slate-500",
        border:       "border-[#30363d]",
        input:        "bg-[#0d1117] border-[#30363d] text-slate-100 focus:border-[#D9531E]",
        btnGhost:     "bg-[#161b22] hover:bg-slate-800 text-slate-300 border-[#30363d]",
        badge:        "bg-slate-800 text-slate-300",
        divider:      "divide-[#30363d]",
        hoverRow:     "hover:bg-slate-800/60",
        alertRed:     "bg-red-950/30 border-red-900/50 text-red-300",
        alertPurple:  "bg-violet-950/30 border-violet-900/50 text-violet-300",
        alertIndigo:  "bg-orange-950/30 border-orange-900/50 text-[#D9531E]",
        alertTeal:    "bg-teal-950/30 border-teal-900/50 text-teal-300",
        grid:         "#30363d",
        axis:         "#94A3B8",
        tooltipBg:    "#161b22",
        tooltipBorder:"#30363d",
        tooltipText:  "#F1F5F9",
      }
    : {
        page:         "bg-[#F8FAFC] dark:bg-[#0d1117] text-slate-900 dark:text-slate-100",
        card:         "bg-white dark:bg-[#161b22] border-slate-200/80 dark:border-[#30363d] shadow-xs text-slate-900 dark:text-white",
        cardHover:    "hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-sm",
        subheader:    "bg-slate-50/70 dark:bg-[#21262d] border-b border-slate-200/80 dark:border-[#30363d]",
        text:         "text-slate-900 dark:text-white",
        textMuted:    "text-slate-500 dark:text-slate-400",
        textFaint:    "text-slate-400 dark:text-slate-500",
        border:       "border-slate-200/80 dark:border-[#30363d]",
        input:        "bg-white dark:bg-[#0d1117] border-slate-200 dark:border-[#30363d] text-slate-900 dark:text-white focus:border-[#D9531E]",
        btnGhost:     "bg-white dark:bg-[#161b22] hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200/80 dark:border-[#30363d]",
        badge:        "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300",
        divider:      "divide-slate-100 dark:divide-[#30363d]",
        hoverRow:     "hover:bg-slate-50/70 dark:hover:bg-slate-800/60",
        alertRed:     "bg-red-50 dark:bg-red-950/30 border-red-200/80 dark:border-red-900/50 text-red-700 dark:text-red-300",
        alertPurple:  "bg-violet-50 dark:bg-violet-950/30 border-violet-200/80 dark:border-violet-900/50 text-violet-700 dark:text-violet-300",
        alertIndigo:  "bg-orange-50 dark:bg-orange-950/30 border-orange-200/80 text-[#D9531E]",
        alertTeal:    "bg-teal-50 dark:bg-teal-950/30 border-teal-200/80 text-teal-700 dark:text-teal-300",
        grid:         "#f1f5f9",
        axis:         "#94a3b8",
        tooltipBg:    "#ffffff",
        tooltipBorder:"#e2e8f0",
        tooltipText:  "#0f172a",
      };

// ═══════════════════════════════════════════════════════════════════════════════
// ■ SHARED UI COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

// ─── STAT CARD (Reference Design) ─────────────────────────────────────────────
const StatCard = ({ title, value, subtext, icon: Icon, color, onClick, t }) => (
  <div
    onClick={onClick}
    className={`${t.card} border rounded-2xl p-4 sm:p-5 shadow-xs hover:shadow-md overflow-hidden group cursor-pointer transition-all duration-200 flex flex-col justify-between h-full hover:-translate-y-0.5`}
  >
    {/* Top Row: Icon + Count/Title + Subtext */}
    <div className="flex justify-between items-start gap-3">
      <div className="flex items-center gap-3.5 min-w-0">
        <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center shrink-0 ${color.bg} dark:bg-slate-800/80 transition-transform duration-200 group-hover:scale-105`}>
          <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${color.text} dark:text-slate-200`} strokeWidth={1.8} />
        </div>
        <div className="min-w-0">
          <h3 className={`text-2xl sm:text-[26px] font-black tracking-tight ${t.text} truncate tabular-nums leading-none`}>
            {value}
          </h3>
          <p className={`text-xs sm:text-[13px] font-medium ${t.textMuted} mt-1 capitalize leading-tight truncate`}>
            {title}
          </p>
        </div>
      </div>
      {subtext && (
        <span className="text-[10px] font-semibold text-gray-500 dark:text-slate-400 bg-gray-100/80 dark:bg-slate-800 px-2 py-0.5 rounded-md shrink-0">
          {subtext}
        </span>
      )}
    </div>

    {/* Bottom Row: Divider + "View Details" + External Link Icon */}
    <div className={`border-t ${t.border} mt-3.5 pt-2.5 flex items-center justify-between`}>
      <span className={`text-[11px] sm:text-xs font-semibold ${t.textFaint} group-hover:${color.text} transition-colors`}>
        View Details
      </span>
      <ArrowTopRightOnSquareIcon className={`w-3.5 h-3.5 ${t.textFaint} group-hover:${color.text} group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-150`} />
    </div>
  </div>
);

// ─── KPI CARD ─────────────────────────────────────────────────────────────────
const KPICard = ({ label, value, growth, suffix = "", color = "indigo", t }) => {
  const isPos = (Number(growth) || 0) >= 0;
  const palettes = {
    indigo:  { val: "text-indigo-500 dark:text-indigo-400",  ring: "ring-indigo-500/20"  },
    emerald: { val: "text-emerald-500 dark:text-emerald-400", ring: "ring-emerald-500/20" },
    amber:   { val: "text-amber-500 dark:text-amber-400",   ring: "ring-amber-500/20"   },
    rose:    { val: "text-rose-500 dark:text-rose-400",    ring: "ring-rose-500/20"    },
    cyan:    { val: "text-cyan-500 dark:text-cyan-400",    ring: "ring-cyan-500/20"    },
  };
  const c = palettes[color] || palettes.indigo;
  return (
    <div className={`${t.card} ${t.cardHover} border rounded-2xl p-4 ring-1 ${c.ring} transition-all duration-200`}>
      <p className={`text-[10px] font-extrabold uppercase tracking-widest ${t.textFaint} mb-1.5`}>{label}</p>
      <p className={`text-xl font-black tabular-nums ${c.val}`}>{value}{suffix}</p>
      {growth !== undefined && growth !== null && (
        <div className="flex items-center gap-1.5 mt-2">
          <span className={`inline-flex items-center gap-0.5 text-[11px] font-bold px-2 py-0.5 rounded-full ${isPos ? "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400" : "bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400"}`}>
            {isPos ? <ArrowUpIcon className="w-3 h-3" /> : <ArrowDownIcon className="w-3 h-3" />}
            {Math.abs(growth).toFixed(1)}%
          </span>
          <span className={`text-[10px] ${t.textFaint}`}>vs prev</span>
        </div>
      )}
    </div>
  );
};

// ─── CHART CARD ───────────────────────────────────────────────────────────────
const ChartCard = ({ title, subtitle, icon: Icon, children, actions, t, className = "" }) => (
  <div className={`${t.card} ${t.cardHover} border rounded-2xl overflow-hidden shadow-sm transition-all duration-200 flex flex-col ${className}`}>
    <div className={`px-5 py-3.5 border-b ${t.subheader} ${t.border} flex justify-between items-center gap-3`}>
      <div className="flex items-center gap-2 min-w-0">
        {Icon && <Icon className={`w-4 h-4 ${t.textMuted} shrink-0`} />}
        <div className="min-w-0">
          <h6 className={`font-bold text-sm ${t.text} truncate`}>{title}</h6>
          {subtitle && <p className={`text-[11px] ${t.textFaint}`}>{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="shrink-0">{actions}</div>}
    </div>
    <div className="flex-1 p-3 md:p-4">{children}</div>
  </div>
);

// ─── CUSTOM TOOLTIP ───────────────────────────────────────────────────────────
const CustomTip = ({ active, payload, label, t, valueFmt }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: t.tooltipBg,
        border: `1px solid ${t.tooltipBorder}`,
        borderRadius: 12,
        padding: "10px 14px",
        minWidth: 130,
        boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
      }}
    >
      {label && (
        <p style={{ color: t.axis, fontSize: 10, fontWeight: 700, marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>
          {label}
        </p>
      )}
      {payload.map((p, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color, flexShrink: 0, display: "inline-block" }} />
          <span style={{ color: t.axis, fontSize: 11 }}>{p.name}:</span>
          <span style={{ color: t.tooltipText, fontSize: 12, fontWeight: 700 }}>
            {valueFmt ? valueFmt(p.value, p.name) : (p.value?.toLocaleString?.("en-IN") ?? p.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

// ─── DATE RANGE SELECTOR ──────────────────────────────────────────────────────
const DateRangeSelector = ({ value, onChange, t }) => (
  <div className={`flex items-center rounded-xl border ${t.border} overflow-hidden`}>
    {[["7d","7D"],["30d","30D"],["90d","3M"],["365d","1Y"]].map(([k, lbl]) => (
      <button
        key={k}
        onClick={() => onChange(k)}
        className={`px-3 py-1.5 text-xs font-bold transition-all ${
          value === k ? "bg-indigo-600 text-white" : `${t.btnGhost} border-0`
        }`}
      >
        {lbl}
      </button>
    ))}
  </div>
);

// ─── SKELETON ─────────────────────────────────────────────────────────────────
const Skeleton = ({ t, className = "h-64" }) => (
  <div className={`${className} rounded-2xl animate-pulse ${t.grid === "#1e293b" ? "bg-slate-800" : "bg-slate-100"}`} />
);

// ─── DRILL BUTTON ─────────────────────────────────────────────────────────────
const DrillBtn = ({ label = "Details →", to, navigate }) => (
  <button onClick={() => navigate(to)} className="text-xs font-bold text-indigo-500 hover:text-indigo-400 transition-colors">
    {label}
  </button>
);

// ═══════════════════════════════════════════════════════════════════════════════
// ■ CHART COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

// ── 1. PROJECT STATUS PIE ─────────────────────────────────────────────────────
const ProjectStatusChart = ({ projects, t, navigate }) => {
  const data = useMemo(() => {
    if (!projects.length) {
      return [
        { name: "Ongoing",   value: 6, color: "#6366f1" },
        { name: "Completed", value: 9, color: "#10b981" },
        { name: "Planning",  value: 3, color: "#f59e0b" },
        { name: "On Hold",   value: 2, color: "#f43f5e" },
      ];
    }
    const map = {};
    projects.forEach((p) => { const s = p.status || "Unknown"; map[s] = (map[s] || 0) + 1; });
    return Object.entries(map).map(([name, value], i) => ({ name, value, color: PALETTE[i % PALETTE.length] }));
  }, [projects]);

  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <ChartCard
      title="Project Status"
      subtitle={`${total} total projects`}
      icon={BriefcaseIcon}
      t={t}
      actions={<DrillBtn to="/admin/projects" navigate={navigate} />}
    >
      <ResponsiveContainer width="100%" height={230}>
        <PieChart>
          <Pie
            data={data} cx="50%" cy="45%"
            innerRadius={56} outerRadius={90}
            paddingAngle={3} dataKey="value"
            onClick={() => navigate("/admin/projects")}
            style={{ cursor: "pointer" }}
          >
            {data.map((d, i) => <Cell key={i} fill={d.color} stroke="transparent" />)}
          </Pie>
          <Tooltip content={<CustomTip t={t} />} />
          <Legend
            iconType="circle" iconSize={8} layout="horizontal" verticalAlign="bottom"
            formatter={(v) => <span style={{ color: t.axis, fontSize: 11, fontWeight: 600 }}>{v}</span>}
          />
        </PieChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

// ── 2. MONTHLY REVENUE AREA ───────────────────────────────────────────────────
// const RevenueChart = ({ purchaseOrders, dateRange, t, navigate }) => {
//   const data = useMemo(() => {
//     const threshold = getDateThreshold(dateRange);
//     const map = {};
//     MONTHS.forEach((m) => { map[m] = { month: m, revenue: 0, cost: 0 }; });

//     let hasReal = false;
//     purchaseOrders.forEach((po) => {
//       const d = parseDate(po.date || po.createdAt || po.orderDate);
//       if (!d || d < threshold) return;
//       hasReal = true;
//       const m = MONTHS[new Date(d).getMonth()];
//       map[m].revenue += Number(po.totalAmount || po.amount || 0);
//       map[m].cost    += Number(po.totalAmount || po.amount || 0) * 0.72;
//     });

//     if (!hasReal) return MOCK_REVENUE.slice(-8);
//     return Object.values(map).filter((m) => m.revenue > 0);
//   }, [purchaseOrders, dateRange]);

//   return (
//     <ChartCard
//       title="Revenue & Cost Trend"
//       subtitle="Monthly financial performance"
//       icon={CurrencyDollarIcon}
//       t={t}
//       actions={<DrillBtn to="/admin/BudgetPage" navigate={navigate} />}
//     >
//       <ResponsiveContainer width="100%" height={230}>
//         <AreaChart data={data} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
//           <defs>
//             <linearGradient id="gRevenue" x1="0" y1="0" x2="0" y2="1">
//               <stop offset="5%"  stopColor="#6366f1" stopOpacity={0.28} />
//               <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
//             </linearGradient>
//             <linearGradient id="gCost" x1="0" y1="0" x2="0" y2="1">
//               <stop offset="5%"  stopColor="#f43f5e" stopOpacity={0.22} />
//               <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} />
//             </linearGradient>
//           </defs>
//           <CartesianGrid strokeDasharray="3 3" stroke={t.grid} />
//           <XAxis dataKey="month" tick={{ fill: t.axis, fontSize: 11 }} axisLine={false} tickLine={false} />
//           <YAxis
//             tick={{ fill: t.axis, fontSize: 10 }} axisLine={false} tickLine={false}
//             tickFormatter={(v) => `₹${(v / 1e5).toFixed(0)}L`}
//           />
//           <Tooltip content={<CustomTip t={t} valueFmt={(v) => fmt.lk(v)} />} />
//           <Legend
//             iconType="circle" iconSize={8}
//             formatter={(v) => <span style={{ color: t.axis, fontSize: 11, fontWeight: 600 }}>{v}</span>}
//           />
//           <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#6366f1" strokeWidth={2.5} fill="url(#gRevenue)" dot={false} />
//           <Area type="monotone" dataKey="cost"    name="Cost"    stroke="#f43f5e" strokeWidth={2}   fill="url(#gCost)"    dot={false} />
//         </AreaChart>
//       </ResponsiveContainer>
//     </ChartCard>
//   );
// };

// ── 3. BUDGET VS ACTUAL BAR ───────────────────────────────────────────────────
const BudgetVsActualChart = ({ projects, t, navigate }) => {
  const data = useMemo(() => {
    const src = projects.filter((p) => Number(p.budget) > 0).slice(0, 6);
    if (!src.length) return MOCK_PROJECTS_BUDGET;
    return src.map((p) => ({
      name:   (p.projectName || p.name || "Project").slice(0, 11),
      budget: Number(p.budget) || 0,
      actual: Number(p.spentAmount || p.actualCost || p.amountSpent || 0) ||
              Math.round((Number(p.budget) || 0) * 0.78),
    }));
  }, [projects]);

  return (
    <ChartCard
      title="Budget vs Actual Cost"
      subtitle="Top project comparisons"
      icon={ChartBarIcon}
      t={t}
      actions={<DrillBtn to="/admin/BudgetPage" navigate={navigate} />}
    >
      <ResponsiveContainer width="100%" height={230}>
        <BarChart data={data} margin={{ top: 5, right: 10, bottom: 22, left: 0 }} barGap={2} barCategoryGap="30%">
          <CartesianGrid strokeDasharray="3 3" stroke={t.grid} vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ fill: t.axis, fontSize: 10 }}
            axisLine={false} tickLine={false}
            angle={-22} textAnchor="end"
          />
          <YAxis
            tick={{ fill: t.axis, fontSize: 10 }} axisLine={false} tickLine={false}
            tickFormatter={(v) => `₹${(v / 1e6).toFixed(0)}M`}
          />
          <Tooltip content={<CustomTip t={t} valueFmt={(v) => fmt.cr(v)} />} />
          <Legend
            iconType="square" iconSize={8}
            formatter={(v) => <span style={{ color: t.axis, fontSize: 11, fontWeight: 600 }}>{v}</span>}
          />
          <Bar dataKey="budget" name="Budget" fill="#6366f1" radius={[4, 4, 0, 0]} />
          <Bar dataKey="actual" name="Actual"  fill="#10b981" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

// ── 4. TASK PROGRESS RADIAL ───────────────────────────────────────────────────
const TaskProgressChart = ({ tasks, t, navigate }) => {
  const data = useMemo(() => {
    const total      = tasks.length || 20;
    const completed  = tasks.filter((tk) => tk.status === "Completed").length || 8;
    const inprog     = tasks.filter((tk) => ["In Progress","InProgress","Ongoing"].includes(tk.status)).length || 6;
    const pending    = tasks.filter((tk) => ["Pending","Todo","Open"].includes(tk.status)).length || 4;
    const blocked    = tasks.filter((tk) => ["Blocked","On Hold"].includes(tk.status)).length || 2;
    const toP = (n) => Math.max(1, Math.round((n / total) * 100));
    return [
      { name: "Completed",   value: toP(completed), fill: "#10b981" },
      { name: "In Progress", value: toP(inprog),    fill: "#6366f1" },
      { name: "Pending",     value: toP(pending),   fill: "#f59e0b" },
      { name: "Blocked",     value: toP(blocked),   fill: "#f43f5e" },
    ];
  }, [tasks]);

  return (
    <ChartCard
      title="Task Completion Rate"
      subtitle="Status distribution"
      icon={ClipboardDocumentCheckIcon}
      t={t}
      actions={<DrillBtn label="View All →" to="/admin/Task" navigate={navigate} />}
    >
      <ResponsiveContainer width="100%" height={230}>
        <RadialBarChart
          cx="50%" cy="48%"
          innerRadius="18%" outerRadius="88%"
          data={data}
          startAngle={180} endAngle={-180}
        >
          <RadialBar
            minAngle={12}
            background
            dataKey="value"
            cornerRadius={5}
            label={{ position: "insideStart", fill: "#fff", fontSize: 10, fontWeight: 800 }}
          />
          <Legend
            iconType="circle" iconSize={8} layout="horizontal" verticalAlign="bottom"
            formatter={(v, e) => (
              <span style={{ color: t.axis, fontSize: 11, fontWeight: 600 }}>
                {v} ({e?.payload?.value}%)
              </span>
            )}
          />
          <Tooltip content={<CustomTip t={t} valueFmt={(v) => `${v}%`} />} />
        </RadialBarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

// ── 5. INVENTORY STOCK LEVELS ─────────────────────────────────────────────────
const InventoryChart = ({ inventories, t, navigate }) => {
  const data = useMemo(() => {
    if (inventories.length < 3) return MOCK_INVENTORY;
    const byCategory = {};
    inventories.forEach((item) => {
      const cat = (item.category || item.itemCategory || "General").slice(0, 10);
      if (!byCategory[cat]) byCategory[cat] = { category: cat, quantity: 0, minStock: 0 };
      byCategory[cat].quantity  += Number(item.quantity || item.currentStock || 0);
      byCategory[cat].minStock   = Math.max(byCategory[cat].minStock, Number(item.reorderLevel || item.minStock || 100));
    });
    return Object.values(byCategory).slice(0, 8);
  }, [inventories]);

  return (
    <ChartCard
      title="Inventory Stock Levels"
      subtitle="Current vs minimum stock"
      icon={CubeIcon}
      t={t}
      actions={<DrillBtn to="/admin/inventory" navigate={navigate} />}
    >
      <ResponsiveContainer width="100%" height={230}>
        <BarChart
          data={data} layout="vertical"
          margin={{ top: 5, right: 20, bottom: 0, left: 40 }}
          barGap={2} barCategoryGap="28%"
        >
          <CartesianGrid strokeDasharray="3 3" stroke={t.grid} horizontal={false} />
          <XAxis type="number" tick={{ fill: t.axis, fontSize: 10 }} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="category" tick={{ fill: t.axis, fontSize: 10 }} axisLine={false} tickLine={false} width={58} />
          <Tooltip content={<CustomTip t={t} valueFmt={(v) => fmt.num(v)} />} />
          <Legend
            iconType="square" iconSize={8}
            formatter={(v) => <span style={{ color: t.axis, fontSize: 11, fontWeight: 600 }}>{v}</span>}
          />
          <Bar dataKey="quantity" name="In Stock"  fill="#06b6d4" radius={[0, 4, 4, 0]} />
          <Bar dataKey="minStock" name="Min Level" fill="#f59e0b" radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

// ── 6. VENDOR PERFORMANCE RADAR ───────────────────────────────────────────────
// const VendorRadarChart = ({ vendors, t, navigate }) => {
//   const { radarData, names } = useMemo(() => {
//     const dims = ["delivery", "quality", "price", "reliability", "comm"];
//     const dimLabels = {
//       delivery: "Delivery", quality: "Quality", price: "Pricing",
//       reliability: "Reliability", comm: "Comm.",
//     };

//     let src = MOCK_VENDOR_RADAR;
//     if (vendors.length >= 2) {
//       src = vendors.slice(0, 3).map((v) => ({
//         vendor:      (v.vendorName || v.name || "Vendor").slice(0, 10),
//         delivery:    Number(v.deliveryRating || v.delivery || 0) || 70 + Math.floor(Math.random() * 25),
//         quality:     Number(v.qualityRating  || v.quality  || 0) || 70 + Math.floor(Math.random() * 25),
//         price:       Number(v.priceRating    || v.price    || 0) || 65 + Math.floor(Math.random() * 25),
//         reliability: Number(v.reliabilityRating             || 0) || 70 + Math.floor(Math.random() * 25),
//         comm:        Number(v.commRating                    || 0) || 68 + Math.floor(Math.random() * 25),
//       }));
//     }

//     const rd = dims.map((dim) => {
//       const entry = { subject: dimLabels[dim] };
//       src.forEach((v) => { entry[v.vendor] = v[dim]; });
//       return entry;
//     });

//     return { radarData: rd, names: src.map((v) => v.vendor) };
//   }, [vendors]);

//   return (
//     <ChartCard
//       title="Vendor Performance"
//       subtitle="Multi-dimension score analysis"
//       icon={UserGroupIcon}
//       t={t}
//       actions={<DrillBtn to="/admin/VendorPage" navigate={navigate} />}
//     >
//       <ResponsiveContainer width="100%" height={230}>
//         <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="68%">
//           <PolarGrid stroke={t.grid} />
//           <PolarAngleAxis dataKey="subject" tick={{ fill: t.axis, fontSize: 10, fontWeight: 600 }} />
//           <PolarRadiusAxis domain={[0, 100]} tick={{ fill: t.axis, fontSize: 9 }} axisLine={false} />
//           {names.map((name, i) => (
//             <Radar
//               key={name} name={name} dataKey={name}
//               stroke={PALETTE[i]} fill={PALETTE[i]} fillOpacity={0.12} strokeWidth={2}
//             />
//           ))}
//           <Tooltip content={<CustomTip t={t} valueFmt={(v) => `${v}/100`} />} />
//           <Legend
//             iconType="circle" iconSize={8}
//             formatter={(v) => <span style={{ color: t.axis, fontSize: 11, fontWeight: 600 }}>{v}</span>}
//           />
//         </RadarChart>
//       </ResponsiveContainer>
//     </ChartCard>
//   );
// };

// ── 7. CRM PIPELINE ───────────────────────────────────────────────────────────
const CRMPipelineChart = ({ columns, t, navigate }) => {
  const data = useMemo(() => {
    const entries = Object.entries(columns);
    if (!entries.length) {
      return [
        { stage: "Lead",        count: 24, value: 18500000 },
        { stage: "Qualified",   count: 16, value: 28700000 },
        { stage: "Proposal",    count: 10, value: 41200000 },
        { stage: "Negotiation", count: 6,  value: 56800000 },
        { stage: "Won",         count: 4,  value: 89400000 },
      ];
    }
    return entries
      .map(([stage, col]) => ({
        stage: stage.slice(0, 12),
        count: (col.items || []).length,
        value: (col.items || []).reduce((s, l) => s + (Number(l.budget) || 0), 0),
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [columns]);

  return (
    <ChartCard
      title="CRM Sales Pipeline"
      subtitle="Leads by stage"
      icon={FunnelIcon}
      t={t}
      actions={<DrillBtn label="View CRM →" to="/crm" navigate={navigate} />}
    >
      <ResponsiveContainer width="100%" height={230}>
        <ComposedChart data={data} layout="vertical" margin={{ top: 5, right: 20, bottom: 0, left: 60 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={t.grid} horizontal={false} />
          <XAxis type="number" tick={{ fill: t.axis, fontSize: 10 }} axisLine={false} tickLine={false} />
          <YAxis
            type="category" dataKey="stage"
            tick={{ fill: t.axis, fontSize: 10 }} axisLine={false} tickLine={false} width={68}
          />
          <Tooltip content={<CustomTip t={t} />} />
          <Legend
            iconType="square" iconSize={8}
            formatter={(v) => <span style={{ color: t.axis, fontSize: 11, fontWeight: 600 }}>{v}</span>}
          />
          <Bar
            dataKey="count" name="Leads" fill="#8b5cf6" radius={[0, 6, 6, 0]}
            background={{ fill: t.grid, radius: [0, 6, 6, 0] }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

// ── 8. EMPLOYEE DISTRIBUTION PIE ──────────────────────────────────────────────
const EmployeeChart = ({ employees, t, navigate }) => {
  const data = useMemo(() => {
    if (employees.length < 3) {
      return [
        { name: "Engineering",  value: 28, color: "#6366f1" },
        { name: "Construction", value: 62, color: "#10b981" },
        { name: "Admin",        value: 12, color: "#f59e0b" },
        { name: "Safety",       value: 8,  color: "#f43f5e" },
        { name: "Equipment",    value: 15, color: "#06b6d4" },
        { name: "Procurement",  value: 7,  color: "#8b5cf6" },
      ];
    }
    const map = {};
    employees.forEach((e) => {
      const dept = (e.department || e.dept || e.role || "Other");
      map[dept] = (map[dept] || 0) + 1;
    });
    return Object.entries(map).map(([name, value], i) => ({ name, value, color: PALETTE[i % PALETTE.length] }));
  }, [employees]);

  return (
    <ChartCard
      title="Employee Distribution"
      subtitle="Workforce by department"
      icon={UsersIcon}
      t={t}
      actions={<DrillBtn label="View HR →" to="/admin/hrm/employees" navigate={navigate} />}
    >
      <ResponsiveContainer width="100%" height={230}>
        <PieChart>
          <Pie
            data={data} cx="50%" cy="45%"
            outerRadius={82} paddingAngle={2} dataKey="value"
            onClick={() => navigate("/admin/hrm/employees")}
            style={{ cursor: "pointer" }}
          >
            {data.map((d, i) => <Cell key={i} fill={d.color} stroke="transparent" />)}
          </Pie>
          <Tooltip content={<CustomTip t={t} />} />
          <Legend
            iconType="circle" iconSize={8} layout="horizontal" verticalAlign="bottom"
            formatter={(v) => <span style={{ color: t.axis, fontSize: 11, fontWeight: 600 }}>{v}</span>}
          />
        </PieChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

// ── 9. EQUIPMENT UTILIZATION ──────────────────────────────────────────────────
const EquipmentUsageChart = ({ equipmentList, t, navigate }) => {
  const data = useMemo(() => {
    if (equipmentList.length < 3) return MOCK_EQUIPMENT_USAGE;
    const typeMap = {};
    equipmentList.forEach((e) => {
      const type = (e.type || e.equipmentType || e.category || "Other").slice(0, 12);
      if (!typeMap[type]) typeMap[type] = { type, active: 0, total: 0 };
      typeMap[type].total++;
      if (["Active", "Deployed", "In Use"].includes(e.status)) typeMap[type].active++;
    });
    return Object.values(typeMap).slice(0, 6).map((r) => ({
      type:         r.type,
      utilization:  r.total > 0 ? Math.round((r.active  / r.total) * 100) : 0,
      idle:         r.total > 0 ? Math.round(((r.total - r.active) / r.total) * 100) : 0,
    }));
  }, [equipmentList]);

  return (
    <ChartCard
      title="Equipment Utilization"
      subtitle="Active vs idle by type"
      icon={TruckIcon}
      t={t}
      actions={<DrillBtn to="/admin/equipment/manager" navigate={navigate} />}
    >
      <ResponsiveContainer width="100%" height={230}>
        <BarChart data={data} margin={{ top: 5, right: 10, bottom: 22, left: 0 }} barSize={20}>
          <CartesianGrid strokeDasharray="3 3" stroke={t.grid} vertical={false} />
          <XAxis
            dataKey="type"
            tick={{ fill: t.axis, fontSize: 10 }} axisLine={false} tickLine={false}
            angle={-20} textAnchor="end"
          />
          <YAxis
            domain={[0, 100]}
            tick={{ fill: t.axis, fontSize: 10 }} axisLine={false} tickLine={false}
            tickFormatter={(v) => `${v}%`}
          />
          <Tooltip content={<CustomTip t={t} valueFmt={(v) => `${v}%`} />} />
          <Legend
            iconType="square" iconSize={8}
            formatter={(v) => <span style={{ color: t.axis, fontSize: 11, fontWeight: 600 }}>{v}</span>}
          />
          <Bar dataKey="utilization" name="Utilization" stackId="a" fill="#14b8a6" radius={[4, 4, 0, 0]} />
          <Bar dataKey="idle"        name="Idle"        stackId="a" fill="#f59e0b" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

// ── 10. PURCHASE TRENDS ───────────────────────────────────────────────────────
const PurchaseTrendsChart = ({ purchaseOrders, purchaseRequests, dateRange, t, navigate }) => {
  const data = useMemo(() => {
    const threshold = getDateThreshold(dateRange);
    const map = {};
    MONTHS.forEach((m) => { map[m] = { month: m, poValue: 0, prCount: 0 }; });

    let hasReal = false;
    purchaseOrders.forEach((po) => {
      const d = parseDate(po.date || po.createdAt || po.orderDate);
      if (!d || d < threshold) return;
      hasReal = true;
      const m = MONTHS[new Date(d).getMonth()];
      map[m].poValue += Number(po.totalAmount || po.amount || 0);
    });
    purchaseRequests.forEach((pr) => {
      const d = parseDate(pr.date || pr.createdAt);
      if (!d || d < threshold) return;
      const m = MONTHS[new Date(d).getMonth()];
      map[m].prCount++;
    });

    if (!hasReal) {
      return MOCK_REVENUE.slice(-8).map((m) => ({
        month:   m.month,
        poValue: m.po,
        prCount: Math.floor(m.po / 80000),
      }));
    }
    return Object.values(map).filter((m) => m.poValue > 0 || m.prCount > 0);
  }, [purchaseOrders, purchaseRequests, dateRange]);

  return (
    <ChartCard
      title="Purchase Order Trends"
      subtitle="Procurement spend & request volume"
      icon={ShoppingCartIcon}
      t={t}
      actions={<DrillBtn to="/admin/material/purchase" navigate={navigate} />}
    >
      <ResponsiveContainer width="100%" height={230}>
        <ComposedChart data={data} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id="gPO" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor="#f97316" stopOpacity={0.28} />
              <stop offset="95%" stopColor="#f97316" stopOpacity={0}    />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={t.grid} />
          <XAxis dataKey="month" tick={{ fill: t.axis, fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis
            yAxisId="left"
            tick={{ fill: t.axis, fontSize: 10 }} axisLine={false} tickLine={false}
            tickFormatter={(v) => `₹${(v / 1e5).toFixed(0)}L`}
          />
          <YAxis
            yAxisId="right" orientation="right"
            tick={{ fill: t.axis, fontSize: 10 }} axisLine={false} tickLine={false}
          />
          <Tooltip content={<CustomTip t={t} />} />
          <Legend
            iconType="circle" iconSize={8}
            formatter={(v) => <span style={{ color: t.axis, fontSize: 11, fontWeight: 600 }}>{v}</span>}
          />
          <Area
            yAxisId="left" type="monotone"
            dataKey="poValue" name="PO Value"
            stroke="#f97316" strokeWidth={2.5} fill="url(#gPO)" dot={false}
          />
          <Bar
            yAxisId="right"
            dataKey="prCount" name="PR Count"
            fill="#6366f1" radius={[3, 3, 0, 0]} opacity={0.75}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ■ MAIN DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════════
export default function Dashboard() {
  const navigate = useNavigate();

  // ── UI STATE ──────────────────────────────────────────────────────────────
  const { theme } = useContext(SettingsContext) || {};
  const [isDark, setIsDark] = useState(() => {
    if (typeof document !== "undefined") {
      return document.documentElement.classList.contains("dark") || theme === "dark";
    }
    return theme === "dark";
  });

  useEffect(() => {
    const updateTheme = () => {
      const darkActive = typeof document !== "undefined" && (
        document.documentElement.classList.contains("dark") || theme === "dark"
      );
      setIsDark(darkActive);
    };
    updateTheme();
    const observer = new MutationObserver(updateTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, [theme]);

  const [dateRange,    setDateRange]    = useState("365d");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshKey,   setRefreshKey]   = useState(0);

  const t = useMemo(() => makeTheme(isDark), [isDark]);

  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    setRefreshKey((k) => k + 1);
    setTimeout(() => setIsRefreshing(false), 900);
  }, []);

  // ── AUTH ──────────────────────────────────────────────────────────────────
  const { user, checkAccess } = useAuth();

  // ── SERVER-SIDE EXECUTIVE SUMMARY (On-Demand Fetch) ───────────────────────
  const [dashboardSummary, setDashboardSummary] = useState(null);

  useEffect(() => {
    let isMounted = true;
    api.get('/dashboard/summary')
      .then(res => {
        if (isMounted && res.data) {
          setDashboardSummary(res.data.data || res.data);
        }
      })
      .catch(err => {
        console.warn('Dashboard summary fetch fallback to context data:', err?.message);
      });
    return () => { isMounted = false; };
  }, [refreshKey]);

  // ── CONTEXT DATA ──────────────────────────────────────────────────────────
  const { projects       = [] } = useProjects()      || {};
  const { tasks          = [] } = useTasks()         || {};
  const { purchaseOrders = [], purchaseRequests = [] } = usePurchaseOrder() || {};
  const { vendors        = [] } = useVendors()       || {};
  const { inventories    = [], scrapList = [], warehouses = [] } = useInventory() || {};
  const { columns        = {} } = useCRM()           || {};
  const { equipmentList  = [] } = useEquipment()     || {};
  const { issuances      = [] } = useConsumption()   || {};
  const { employees      = [] } = useEmployees()     || {};

  // ── STATS (original logic, fully preserved, enhanced with server summary) ───
  const stats = useMemo(() => {
    const data = {
      activeProjects:   0, completedProjects: 0,
      myPendingTasks:   0, completedTasks:    0, totalTasks: 0,
      totalBudget:      0, revenue:           0,
      pendingPRs:       0, lowStock:          0,
      activeVendors:    0, crmLeads:          0, crmValue: 0,
      activeEquipment:  0, totalEquipment:    0,
      pendingIndents:   0, scrapItems:        0,
      activeEmployees:  0, warehousesCount:   0,
      recentPOs:        [],
    };

    if (checkAccess("financial.view"))
      data.totalBudget = projects.reduce((s, p) => s + (Number(p.budget) || 0), 0);

    if (checkAccess("project.view")) {
      data.activeProjects    = projects.filter((p) => p.status === "Ongoing").length;
      data.completedProjects = projects.filter((p) => p.status === "Completed").length;
    }

    if (checkAccess("task.view")) {
      data.myPendingTasks = tasks.filter((t) => t.status !== "Completed").length;
      data.completedTasks = tasks.filter((t) => t.status === "Completed").length;
      data.totalTasks     = tasks.length;
    }

    // 🟢 Seamlessly augment with server-aggregated /api/v1/dashboard/summary if available
    if (dashboardSummary) {
      if (dashboardSummary.projects) {
        if (dashboardSummary.projects.totalBudget) data.totalBudget = dashboardSummary.projects.totalBudget;
        if (dashboardSummary.projects.active !== undefined) data.activeProjects = dashboardSummary.projects.active;
        if (dashboardSummary.projects.completed !== undefined) data.completedProjects = dashboardSummary.projects.completed;
      }
      if (dashboardSummary.tasks) {
        if (dashboardSummary.tasks.total !== undefined) data.totalTasks = dashboardSummary.tasks.total;
        if (dashboardSummary.tasks.pending !== undefined) data.myPendingTasks = dashboardSummary.tasks.pending;
        if (dashboardSummary.tasks.completed !== undefined) data.completedTasks = dashboardSummary.tasks.completed;
      }
      if (dashboardSummary.workforce && dashboardSummary.workforce.total !== undefined) {
        data.activeEmployees = dashboardSummary.workforce.total;
      }
    }

    if (checkAccess("purchase.view")) {
      data.pendingPRs = purchaseRequests.filter((pr) => pr.status === "Pending").length;
      data.recentPOs  = purchaseOrders.slice(-5).reverse();
    }

    if (checkAccess("inventory.view"))
      data.lowStock = inventories.filter((i) => i.status === "Low Stock" || i.status === "Out of Stock").length;

    if (checkAccess("warehouse.view"))
      data.warehousesCount = warehouses.length;

    if (checkAccess("inventory.picking"))
      data.pendingIndents = issuances.length;

    if (checkAccess("inventory.scrap"))
      data.scrapItems = scrapList ? scrapList.length : 0;

    if (checkAccess("equipment.view")) {
      data.activeEquipment = equipmentList.filter((e) => e.status === "Active" || e.status === "Deployed").length;
      data.totalEquipment  = equipmentList.length;
    }

    if (checkAccess("crm.view")) {
      const allLeads = Object.values(columns).flatMap((col) => col.items || []);
      data.crmLeads = allLeads.length;
      if (checkAccess("financial.view"))
        data.crmValue = allLeads.reduce((s, l) => s + (Number(l.budget) || 0), 0);
    }

    if (checkAccess("vendor.view"))
      data.activeVendors = vendors.filter((v) => v.vendorStatus === "Active").length;

    if (checkAccess("employee.view"))
      data.activeEmployees = employees.length;

    return data;
  }, [
    projects, tasks, purchaseOrders, purchaseRequests, inventories,
    columns, equipmentList, issuances, scrapList, warehouses, employees, checkAccess,
  ]);

  // ── KPI GROWTH METRICS ─────────────────────────────────────────────────────
  const kpi = useMemo(() => {
    const threshold     = getDateThreshold(dateRange);
    const prevThreshold = getPrevThreshold(dateRange);

    const sumPO = (arr) =>
      arr.reduce((s, po) => s + (Number(po.totalAmount || po.amount) || 0), 0);

    const curPOs  = purchaseOrders.filter((po) => parseDate(po.date || po.createdAt || po.orderDate) >= threshold);
    const prevPOs = purchaseOrders.filter((po) => {
      const d = parseDate(po.date || po.createdAt || po.orderDate);
      return d >= prevThreshold && d < threshold;
    });

    const curVal  = sumPO(curPOs);
    const prevVal = sumPO(prevPOs);
    const poGrowth = prevVal > 0 ? ((curVal - prevVal) / prevVal) * 100 : null;

    const completionPct =
      stats.totalTasks > 0 ? (stats.completedTasks / stats.totalTasks) * 100 : 42;

    const totalSpent = projects.reduce(
      (s, p) => s + (Number(p.spentAmount || p.actualCost || p.amountSpent) || 0), 0,
    );
    const budgetUtil =
      stats.totalBudget > 0 ? (totalSpent / stats.totalBudget) * 100 : 68.4;

    const equipUtil =
      stats.totalEquipment > 0
        ? (stats.activeEquipment / stats.totalEquipment) * 100
        : 73.2;

    const avgVendor =
      vendors.length > 0
        ? vendors.reduce((s, v) => s + (Number(v.rating || v.vendorRating || v.score) || 75), 0) /
          vendors.length
        : 82.5;

    return { curVal, poGrowth, completionPct, budgetUtil, equipUtil, avgVendor };
  }, [purchaseOrders, stats, projects, vendors, dateRange]);

  // ── WIDGET REGISTRY (unchanged) ───────────────────────────────────────────
//   const ACTION_BUTTONS = [
//     { label: "New Project",      icon: PlusIcon,       path: "/admin/projects/add",          perm: "project.create",    color: "bg-indigo-600 hover:bg-indigo-700"  },
//     { label: "New Lead",         icon: UserGroupIcon,   path: "/crm",                          perm: "crm.create",        color: "bg-pink-600 hover:bg-pink-700"      },
//     { label: "New PO",           icon: ShoppingCartIcon,path: "/purchase-requests/create",    perm: "purchase.create",   color: "bg-violet-600 hover:bg-violet-700"  },
//     { label: "Request Material", icon: CubeIcon,        path: "/admin/material/indent",       perm: "inventory.picking", color: "bg-amber-600 hover:bg-amber-700"    },
//     { label: "Add Vendor",       icon: UserGroupIcon,   path: "/admin/VendorPage/add",        perm: "vendor.manage",     color: "bg-cyan-600 hover:bg-cyan-700"      },
//     { label: "Report Safety",    icon: ShieldCheckIcon, path: "/admin/compliance-safety/add", perm: "compliance.manage", color: "bg-emerald-600 hover:bg-emerald-700"},
//   ];

  const ALL_WIDGETS = [
    { id:"crm_leads",       permission:"crm.view",         title:"Active Leads",      value:stats.crmLeads,          subtext:"Sales Pipeline",    icon:FunnelIcon,                 color:{bg:"bg-pink-50",   text:"text-pink-600"},   path:"/crm"                            },
    { id:"finance_budget",  permission:"financial.view",   title:"Total Budget",      value:fmt.cr(stats.totalBudget),subtext:"Allocated Funds",   icon:CurrencyDollarIcon,         color:{bg:"bg-emerald-50",text:"text-emerald-600"},path:"/admin/BudgetPage"               },
    { id:"projects_active", permission:"project.view",     title:"Active Projects",   value:stats.activeProjects,    subtext:"Sites Running",     icon:BriefcaseIcon,              color:{bg:"bg-blue-50",   text:"text-blue-600"},   path:"/admin/projects"                 },
    { id:"tasks_pending",   permission:"task.view",        title:"Pending Tasks",     value:stats.myPendingTasks,    subtext:"Action Items",      icon:ClipboardDocumentCheckIcon, color:{bg:"bg-orange-50", text:"text-orange-600"}, path:"/admin/Task"                     },
    { id:"equipment_status",permission:"equipment.view",   title:"Equipment",         value:stats.activeEquipment,   subtext:"Active & Deployed", icon:TruckIcon,                  color:{bg:"bg-teal-50",   text:"text-teal-600"},   path:"/admin/equipment/manager"        },
    { id:"internal_picking",permission:"inventory.picking",title:"Material Indents",  value:stats.pendingIndents,    subtext:"Stock Requests",    icon:ArrowPathIcon,              color:{bg:"bg-indigo-50", text:"text-indigo-600"}, path:"/admin/material/internal-picking"},
    { id:"compliance_check",permission:"compliance.view",  title:"Safety Compliance", value:"Active",                subtext:"View Records",      icon:ShieldCheckIcon,            color:{bg:"bg-green-50",  text:"text-green-600"},  path:"/admin/compliance-safety"        },
    { id:"procurement_prs", permission:"purchase.view",    title:"Pending PRs",       value:stats.pendingPRs,        subtext:"Purchase Requests", icon:DocumentTextIcon,           color:{bg:"bg-purple-50", text:"text-purple-600"}, path:"/admin/material/purchase"        },
    { id:"inventory_alerts",permission:"inventory.view",   title:"Stock Alerts",      value:stats.lowStock,          subtext:"Low Stock Items",   icon:ExclamationTriangleIcon,    color:{bg:"bg-red-50",    text:"text-red-600"},    path:"/admin/inventory"                },
    { id:"warehouse_view",  permission:"warehouse.view",   title:"Warehouses",        value:stats.warehousesCount,   subtext:"Storage Locations", icon:BuildingOfficeIcon,         color:{bg:"bg-gray-100",  text:"text-gray-600"},   path:"/admin/warehouse-management"     },
    { id:"scrap_view",      permission:"inventory.scrap",  title:"Scrap Items",       value:stats.scrapItems,        subtext:"Disposal Pending",  icon:TrashIcon,                  color:{bg:"bg-rose-50",   text:"text-rose-600"},   path:"/admin/material/scrap"           },
    { id:"employee_view",   permission:"employee.view",    title:"Employees",         value:stats.activeEmployees,   subtext:"Total Workforce",   icon:UsersIcon,                  color:{bg:"bg-cyan-50",   text:"text-cyan-600"},   path:"/admin/hrm/employees"            },
    { id:"doc_view",        permission:"document.view",    title:"Documents",         value:"View",                  subtext:"File Manager",      icon:FolderIcon,                 color:{bg:"bg-yellow-50", text:"text-yellow-600"}, path:"/admin/DocumentPage"             },
    { id:"vendors_active",  permission:"vendor.view",      title:"Vendors",           value:stats.activeVendors,     subtext:"Active Suppliers",  icon:UserGroupIcon,              color:{bg:"bg-blue-50",   text:"text-blue-800"},   path:"/admin/VendorPage"               },
  ];

  const visibleWidgets = ALL_WIDGETS.filter((w) => checkAccess(w.permission));

  const gridClass =
    visibleWidgets.length <= 2  ? "grid-cols-1 md:grid-cols-2"
    : visibleWidgets.length <= 4 ? "grid-cols-2 md:grid-cols-4"
    : visibleWidgets.length <= 6 ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-3"
    : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5";

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div key={refreshKey} className={`${t.page} min-h-screen font-sans`}>
      <div className="max-w-[1600px] mx-auto p-3 sm:p-4 md:p-6 space-y-5">

{/* ══ HEADER (MATCHING 01-dashboard-portfolio.png) ═════════════════════ */}
<div className="flex justify-between items-start sm:items-center gap-2 sm:gap-4 pt-1 pb-1">
  <div className="min-w-0 flex-1 pr-1.5">
    <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 truncate">
      Portfolio · FY 2026–27
    </p>
    <h1 className="text-xl sm:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white font-display mt-0.5 truncate">
      Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 17 ? "afternoon" : "evening"}, {user?.name ? user.name.split(" ")[0] : "Team"}
    </h1>
  </div>

  <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
    {/* Clean Pill Date Range Selector */}
    <div className="flex items-center bg-white dark:bg-[#161b22] border border-slate-200/80 dark:border-[#30363d] rounded-xl p-0.5 sm:p-1 shadow-2xs">
      {["7D", "30D", "3M", "1Y"].map((range) => (
        <button
          key={range}
          onClick={() => setDateRange(range)}
          className={`px-2 sm:px-3 py-1 rounded-lg text-[10px] sm:text-xs font-semibold transition-all ${
            dateRange === range
              ? "bg-slate-900 dark:bg-[#30363d] text-white dark:text-white border border-transparent dark:border-slate-600/50 shadow-2xs"
              : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800"
          }`}
        >
          {range}
        </button>
      ))}
    </div>

    {/* Refresh Button */}
    <button
      onClick={handleRefresh}
      title="Refresh Dashboard"
      className="p-1.5 sm:p-2 bg-white dark:bg-[#161b22] hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white border border-slate-200/80 dark:border-[#30363d] rounded-xl shadow-2xs transition shrink-0"
    >
      <ArrowPathIcon className={`w-4 h-4 ${isRefreshing ? "animate-spin text-[#D9531E]" : ""}`} />
    </button>
  </div>
</div>

{/* ══ 4 HERO PORTFOLIO METRICS (MATCHING 01-dashboard-portfolio.png) ══ */}
<div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4">
  {/* Card 1: Portfolio Budget */}
  <div className="bg-white dark:bg-[#161b22] border border-slate-200/80 dark:border-[#30363d] rounded-xl sm:rounded-2xl p-2.5 sm:p-5 shadow-xs flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
    <div>
      <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 truncate">Portfolio Budget</p>
      <h3 className="text-xl sm:text-3xl font-extrabold text-slate-900 dark:text-white mt-1 sm:mt-2 font-display truncate">
        {stats.totalBudget > 0 ? fmt.cr(stats.totalBudget) : "₹389.4 Cr"}
      </h3>
    </div>
    <p className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 mt-2.5 sm:mt-4 flex items-center gap-1.5 sm:gap-2 truncate">
      <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-emerald-500 shrink-0"></span>
      <span className="truncate">{stats.activeProjects > 0 ? `${stats.activeProjects} active projects` : "12 active projects"}</span>
    </p>
  </div>

  {/* Card 2: Committed */}
  <div className="bg-white dark:bg-[#161b22] border border-slate-200/80 dark:border-[#30363d] rounded-xl sm:rounded-2xl p-2.5 sm:p-5 shadow-xs flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
    <div>
      <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 truncate">Committed</p>
      <h3 className="text-xl sm:text-3xl font-extrabold text-slate-900 dark:text-white mt-1 sm:mt-2 font-display truncate">
        {kpi.curVal > 0 ? fmt.cr(kpi.curVal) : "₹241.7 Cr"}
      </h3>
    </div>
    <p className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 mt-2.5 sm:mt-4 truncate">
      <span className="font-semibold text-slate-700 dark:text-slate-200">
        {kpi.budgetUtil ? `${kpi.budgetUtil.toFixed(1)}%` : "62.1%"}
      </span> of budget
    </p>
  </div>

  {/* Card 3: Weighted Progress */}
  <div className="bg-white dark:bg-[#161b22] border border-slate-200/80 dark:border-[#30363d] rounded-xl sm:rounded-2xl p-2.5 sm:p-5 shadow-xs flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
    <div>
      <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 truncate">Weighted Progress</p>
      <h3 className="text-xl sm:text-3xl font-extrabold text-slate-900 dark:text-white mt-1 sm:mt-2 font-display truncate">
        {kpi.completionPct ? `${kpi.completionPct.toFixed(1)}%` : "68.4%"}
      </h3>
    </div>
    {/* Segmented dot progress bar matching 01-dashboard-portfolio.png */}
    <div className="flex items-center gap-1 sm:gap-1.5 mt-2.5 sm:mt-4">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((step) => {
        const activeCount = Math.round(((kpi.completionPct || 68.4) / 100) * 10);
        const isFilled = step <= activeCount;
        return (
          <span
            key={step}
            className={`h-1.5 sm:h-2 flex-1 rounded-full transition-colors ${
              isFilled ? "bg-slate-900 dark:bg-slate-100" : "bg-slate-200 dark:bg-slate-800"
            }`}
          />
        );
      })}
    </div>
  </div>

  {/* Card 4: Bills on Hold */}
  <div className="bg-white dark:bg-[#161b22] border border-slate-200/80 dark:border-[#30363d] rounded-xl sm:rounded-2xl p-2.5 sm:p-5 shadow-xs flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
    <div>
      <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 truncate">Bills on Hold</p>
      <h3 className="text-xl sm:text-3xl font-extrabold text-[#D9531E] mt-1 sm:mt-2 font-display truncate">
        {stats.pendingPRs > 0 ? stats.pendingPRs : "3"}
      </h3>
    </div>
    <p className="text-[11px] sm:text-xs font-semibold text-[#D9531E] mt-2.5 sm:mt-4 flex items-center gap-1.5 truncate">
      <span className="truncate">₹18.2 L · 3-way mismatch</span>
    </p>
  </div>
</div>

{/* ══ STAGE-GATED PROGRESS & LIVE COST VARIANCE (MATCHING 01-dashboard-portfolio & 03-budget-cost-control) ══ */}
<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
  {/* Stage-Gated Progress Card */}
  <div className="bg-white dark:bg-[#161b22] border border-slate-200/80 dark:border-[#30363d] rounded-2xl p-5 shadow-xs">
    <div className="flex items-center justify-between border-b border-slate-100 dark:border-[#30363d] pb-3 mb-4">
      <div>
        <h3 className="text-sm font-bold text-slate-900 dark:text-white">Stage-Gated Progress</h3>
        <p className="text-[11px] text-slate-400 dark:text-slate-500">Rollup across active towers & site activities</p>
      </div>
      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
        6 Stages
      </span>
    </div>

    <div className="space-y-3">
      {[
        { name: "Planning & Approvals", pct: 100, color: "bg-emerald-600 dark:bg-emerald-500" },
        { name: "Foundation & Piling", pct: 94, color: "bg-emerald-500 dark:bg-emerald-400" },
        { name: "Structure & Slab", pct: 82, color: "bg-slate-900 dark:bg-slate-100" },
        { name: "MEP & Services", pct: 65, color: "bg-[#D9531E]" },
        { name: "Finishing & Facade", pct: 41, color: "bg-amber-500" },
        { name: "Handover & Snagging", pct: 12, color: "bg-slate-400 dark:bg-slate-500" },
      ].map((stage) => (
        <div key={stage.name} className="space-y-1">
          <div className="flex justify-between items-center text-xs">
            <span className="font-medium text-slate-700 dark:text-slate-300">{stage.name}</span>
            <span className="font-bold text-slate-900 dark:text-white">{stage.pct}%</span>
          </div>
          <div className="w-full h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${stage.color}`}
              style={{ width: `${stage.pct}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  </div>

  {/* Budget vs Actual & Variance by Cost Head Card (Reference 03) */}
  <div className="bg-white dark:bg-[#161b22] border border-slate-200/80 dark:border-[#30363d] rounded-2xl p-5 shadow-xs flex flex-col justify-between">
    <div>
      <div className="flex items-center justify-between border-b border-slate-100 dark:border-[#30363d] pb-3 mb-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900 dark:text-white">Budget vs Actual by Cost Head</h3>
          <p className="text-[11px] text-slate-400 dark:text-slate-500">Skyline Residences · Tower B</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Variance at Completion</p>
          <p className="text-sm font-extrabold text-[#D9531E]">-₹2.6 Cr</p>
        </div>
      </div>

      {/* Alert Banner */}
      <div className="bg-amber-50/80 dark:bg-amber-950/30 border border-amber-200/90 dark:border-amber-800/60 rounded-xl p-3 mb-4 flex items-start gap-2.5">
        <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-xs font-bold text-amber-900 dark:text-amber-200">Bulk materials crossed 85% of budget</p>
          <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-0.5">
            Drivers: TMT steel rate +6.4% vs BOQ · RMC wastage 3.1% vs 2% allowed
          </p>
        </div>
      </div>

      {/* Cost Head Progress Bars with 85% alert line */}
      <div className="space-y-3.5">
        {[
          { name: "Direct labour", pct: 72, total: "₹18.40 Cr", committed: 15 },
          { name: "Bulk materials", pct: 88, total: "₹42.10 Cr", committed: 8, warning: true },
          { name: "Heavy plant", pct: 54, total: "₹9.75 Cr", committed: 12 },
          { name: "Subcontract packages", pct: 66, total: "₹26.30 Cr", committed: 18 },
        ].map((item) => (
          <div key={item.name} className="space-y-1">
            <div className="flex justify-between items-center text-xs">
              <span className="font-medium text-slate-700 dark:text-slate-300">{item.name}</span>
              <span className="text-slate-500 dark:text-slate-400">
                <span className={`font-bold ${item.warning ? "text-[#D9531E]" : "text-slate-900 dark:text-white"}`}>{item.pct}%</span> of {item.total}
              </span>
            </div>
            <div className="relative w-full h-3 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden flex">
              {/* Actual bar */}
              <div
                className={`h-full ${item.warning ? "bg-[#D9531E]" : "bg-slate-900 dark:bg-slate-200"} rounded-l-full`}
                style={{ width: `${item.pct - item.committed}%` }}
              />
              {/* Committed bar */}
              <div
                className="h-full bg-slate-400/80 dark:bg-slate-500/80"
                style={{ width: `${item.committed}%` }}
              />
              {/* 85% alert line indicator */}
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
                style={{ left: "85%" }}
                title="85% alert line"
              />
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Legend */}
    <div className="flex items-center gap-4 text-[11px] text-slate-500 dark:text-slate-400 pt-3 border-t border-slate-100 dark:border-[#30363d] mt-4">
      <span className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 rounded-sm bg-slate-900 dark:bg-slate-200"></span> Actual
      </span>
      <span className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 rounded-sm bg-slate-400 dark:bg-slate-500"></span> Committed
      </span>
      <span className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 border-l-2 border-red-500"></span> 85% alert line
      </span>
    </div>
  </div>
</div>

{/* ══ KPI GROWTH ROW ════════════════════════════════════════════════ */}
<div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
  {checkAccess("financial.view") && (
    <KPICard label="Procurement Spend"   value={fmt.lk(kpi.curVal)}              growth={kpi.poGrowth}  color="indigo"  t={t} />
  )}
  {checkAccess("task.view") && (
    <KPICard label="Task Completion"     value={fmt.pct(kpi.completionPct)}                            color="emerald" t={t} />
  )}
  {checkAccess("financial.view") && (
    <KPICard label="Budget Utilization"  value={fmt.pct(kpi.budgetUtil)}                               color="amber"   t={t} />
  )}
  {checkAccess("equipment.view") && (
    <KPICard label="Equip. Utilization"  value={fmt.pct(kpi.equipUtil)}                                color="cyan"    t={t} />
  )}
  {checkAccess("vendor.view") && (
    <KPICard label="Avg Vendor Score"    value={kpi.avgVendor.toFixed(0)} suffix="/100"                color="rose"    t={t} />
  )}
</div>

        {/* ══ STAT CARDS ════════════════════════════════════════════════════ */}
        {isRefreshing ? (
          <div className={`grid gap-3 ${gridClass}`}>
            {Array(Math.min(visibleWidgets.length, 8)).fill(0).map((_, i) => (
              <Skeleton key={i} t={t} className="h-28 rounded-2xl" />
            ))}
          </div>
        ) : visibleWidgets.length > 0 ? (
          <div className={`grid gap-3 ${gridClass}`}>
            {visibleWidgets.map((widget) => (
              <StatCard
                key={widget.id}
                title={widget.title}
                value={widget.value}
                subtext={widget.subtext}
                icon={widget.icon}
                color={widget.color}
                t={t}
                onClick={() => navigate(widget.path)}
              />
            ))}
          </div>
        ) : (
          <div className={`text-center py-12 ${t.card} border rounded-2xl`}>
            <p className={t.textFaint}>No dashboard widgets available for your role.</p>
          </div>
        )}

        {/* ══ CHARTS ROW 1: Project Status + Revenue ════════════════════════ */}
        {!isRefreshing && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {checkAccess("project.view") && (
                <ProjectStatusChart projects={projects} t={t} navigate={navigate} />
              )}
              {/* {checkAccess("financial.view") && (
                <RevenueChart purchaseOrders={purchaseOrders} dateRange={dateRange} t={t} navigate={navigate} />
              )} */}
              {checkAccess("inventory.view") && (
                <InventoryChart inventories={inventories} t={t} navigate={navigate} />
              )}
            </div>

            {/* ══ CHARTS ROW 2: Budget vs Actual + Task Progress ════════════ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {checkAccess("financial.view") && (
                <BudgetVsActualChart projects={projects} t={t} navigate={navigate} />
              )}
              {checkAccess("task.view") && (
                <TaskProgressChart tasks={tasks} t={t} navigate={navigate} />
              )}
            </div>

            {/* ══ CHARTS ROW 3: Inventory + Vendor Radar ════════════════════ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              
              {/* {checkAccess("vendor.view") && (
                <VendorRadarChart vendors={vendors} t={t} navigate={navigate} />
              )} */}
            </div>

            {/* ══ CHARTS ROW 4: CRM Pipeline + Employee Dist ════════════════ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {checkAccess("crm.view") && (
                <CRMPipelineChart columns={columns} t={t} navigate={navigate} />
              )}
              {checkAccess("employee.view") && (
                <EmployeeChart employees={employees} t={t} navigate={navigate} />
              )}
            </div>

            {/* ══ CHARTS ROW 5: Equipment + Purchase Trends ════════════════ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {checkAccess("equipment.view") && (
                <EquipmentUsageChart equipmentList={equipmentList} t={t} navigate={navigate} />
              )}
              {checkAccess("purchase.view") && (
                <PurchaseTrendsChart
                  purchaseOrders={purchaseOrders}
                  purchaseRequests={purchaseRequests}
                  dateRange={dateRange}
                  t={t}
                  navigate={navigate}
                />
              )}
            </div>
          </>
        )}

        {isRefreshing && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array(4).fill(0).map((_, i) => <Skeleton key={i} t={t} className="h-72 rounded-2xl" />)}
          </div>
        )}

        {/* ══ OPERATIONS DEEP DIVE ══════════════════════════════════════════ */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

          {/* Task List */}
          {checkAccess("task.view") && (
            <div className={`${t.card} border rounded-2xl overflow-hidden shadow-sm flex flex-col lg:col-span-2`}>
              <div className={`px-5 py-3.5 border-b ${t.subheader} ${t.border} flex justify-between items-center`}>
                <h6 className={`font-bold text-sm ${t.text} flex items-center gap-2`}>
                  <ClipboardDocumentCheckIcon className={`w-4 h-4 ${t.textMuted}`} />
                  {checkAccess("project.view") ? "Project Tasks Overview" : "My Assigned Tasks"}
                </h6>
                <button
                  onClick={() => navigate("/admin/Task")}
                  className="text-xs font-bold text-indigo-500 hover:text-indigo-400"
                >
                  VIEW ALL →
                </button>
              </div>
              <div className={`flex-1 overflow-y-auto max-h-72 divide-y ${t.divider}`}>
                {tasks
                  .filter((tk) => tk.status !== "Completed")
                  .slice(0, 6)
                  .map((tk) => (
                    <div
                      key={tk.id}
                      className={`flex items-center justify-between gap-3 px-5 py-3.5 ${t.hoverRow} cursor-pointer transition-colors`}
                      onClick={() => navigate("/admin/Task")}
                    >
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-semibold ${t.text} truncate`}>{tk.title}</p>
                        <p className={`text-xs ${t.textFaint} truncate`}>{tk.projectName || "General Task"}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                          tk.priority === "High"   ? "bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400"
                          : tk.priority === "Medium" ? "bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400"
                          : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
                        }`}>
                          {tk.priority || "Normal"}
                        </span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                          tk.status === "In Progress" ? "bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-400"
                          : tk.status === "Pending"    ? "bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400"
                          : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
                        }`}>
                          {tk.status || "Pending"}
                        </span>
                      </div>
                    </div>
                  ))}
                {tasks.length === 0 && (
                  <div className={`py-10 text-center ${t.textFaint} text-sm`}>No pending tasks.</div>
                )}
              </div>
            </div>
          )}

          {/* Operations Feed */}
          {(checkAccess("inventory.view") || checkAccess("purchase.view") || checkAccess("inventory.picking")) && (
            <div className={`${t.card} border rounded-2xl overflow-hidden shadow-sm flex flex-col`}>
              <div className={`px-5 py-3.5 border-b ${t.subheader} ${t.border} flex items-center gap-2`}>
                <ShoppingCartIcon className={`w-4 h-4 ${t.textMuted}`} />
                <h6 className={`font-bold text-sm ${t.text}`}>Operations Feed</h6>
              </div>

              <div className="p-4 space-y-2.5 flex-1">
                {checkAccess("inventory.view") && stats.lowStock > 0 && (
                  <div className={`flex items-center justify-between p-3.5 rounded-xl border ${t.alertRed}`}>
                    <div className="flex items-center gap-2.5">
                      <ExclamationTriangleIcon className="w-4 h-4 shrink-0" />
                      <div>
                        <p className="text-sm font-bold">Low Stock Alert</p>
                        <p className="text-xs opacity-75">{stats.lowStock} items critical</p>
                      </div>
                    </div>
                    <button onClick={() => navigate("/admin/inventory")} className="text-xs font-black hover:underline shrink-0">Check →</button>
                  </div>
                )}
                {checkAccess("purchase.view") && stats.pendingPRs > 0 && (
                  <div className={`flex items-center justify-between p-3.5 rounded-xl border ${t.alertPurple}`}>
                    <div className="flex items-center gap-2.5">
                      <DocumentTextIcon className="w-4 h-4 shrink-0" />
                      <div>
                        <p className="text-sm font-bold">Pending Requests</p>
                        <p className="text-xs opacity-75">{stats.pendingPRs} PRs waiting</p>
                      </div>
                    </div>
                    <button onClick={() => navigate("/admin/material/purchase")} className="text-xs font-black hover:underline shrink-0">Review →</button>
                  </div>
                )}
                {checkAccess("inventory.picking") && stats.pendingIndents > 0 && (
                  <div className={`flex items-center justify-between p-3.5 rounded-xl border ${t.alertIndigo}`}>
                    <div className="flex items-center gap-2.5">
                      <CubeIcon className="w-4 h-4 shrink-0" />
                      <div>
                        <p className="text-sm font-bold">Material Indents</p>
                        <p className="text-xs opacity-75">{stats.pendingIndents} records</p>
                      </div>
                    </div>
                    <button onClick={() => navigate("/admin/material/internal-picking")} className="text-xs font-black hover:underline shrink-0">View →</button>
                  </div>
                )}
                {checkAccess("equipment.view") && stats.activeEquipment > 0 && (
                  <div className={`flex items-center justify-between p-3.5 rounded-xl border ${t.alertTeal}`}>
                    <div className="flex items-center gap-2.5">
                      <TruckIcon className="w-4 h-4 shrink-0" />
                      <div>
                        <p className="text-sm font-bold">Equipment Active</p>
                        <p className="text-xs opacity-75">{stats.activeEquipment} units deployed</p>
                      </div>
                    </div>
                  </div>
                )}
                {stats.lowStock === 0 && stats.pendingPRs === 0 && stats.pendingIndents === 0 && stats.activeEquipment === 0 && (
                  <div className={`flex flex-col items-center py-8 ${t.textFaint} text-sm`}>
                    <ShieldCheckIcon className="w-10 h-10 mb-2 opacity-25" />
                    <p className="font-semibold">All systems operational.</p>
                  </div>
                )}
              </div>

              {/* Recent POs mini-list */}
              {checkAccess("purchase.view") && stats.recentPOs.length > 0 && (
                <>
                  <div className={`px-4 py-2 border-t ${t.border}`}>
                    <p className={`text-[10px] font-extrabold uppercase tracking-widest ${t.textFaint}`}>Recent Purchase Orders</p>
                  </div>
                  <div className={`divide-y ${t.divider}`}>
                    {stats.recentPOs.slice(0, 3).map((po, i) => (
                      <div
                        key={i}
                        className={`flex justify-between items-center px-4 py-2.5 ${t.hoverRow} cursor-pointer transition-colors text-xs`}
                        onClick={() => navigate("/admin/material/purchase")}
                      >
                        <span className={`font-semibold ${t.text} truncate max-w-[110px]`}>
                          {po.vendorName || po.vendor || `PO-${i + 1}`}
                        </span>
                        <span className="font-bold text-indigo-500">{fmt.lk(po.totalAmount || po.amount)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        
      </div>
    </div>
  );
}

/*
 * ═══════════════════════════════════════════════════════════════════════════
 * REQUIRED DEPENDENCIES — run in your project root:
 *
 *   npm install recharts
 *
 * That's the only new dependency. Everything else (React, react-router-dom,
 * @heroicons/react, tailwindcss) must already be installed.
 *
 * OPTIONAL (for better number formatting):
 *   npm install numeral
 *
 * TAILWIND DARK MODE — ensure your tailwind.config.js has:
 *   module.exports = { darkMode: 'class', ... }
 * (Not required since this file uses inline conditional classes, but needed
 * if you want Tailwind's dark: variants elsewhere in your project.)
 * ═══════════════════════════════════════════════════════════════════════════
 */